import React, { useEffect, useState, memo } from 'react';
import { ArrowRightIcon, BookOpenCheckIcon, BrainCircuitIcon, CalendarCheck2Icon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, ClipboardCheckIcon, CreditCardIcon, FileTextIcon, GraduationCapIcon, InfinityIcon, LockKeyholeIcon, MapPinnedIcon, PackageCheckIcon, PencilLineIcon, PercentIcon, ShieldCheckIcon, SparklesIcon, StarIcon, TimerIcon, TrendingUpIcon, UserRoundCheckIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AccordionItem } from '../components/AccordionItem';
import { MathVisual, MathVisualKind } from '../components/MathVisual';
import { MetaPixel } from '../components/MetaPixel';
import { useScreenInit } from '../useScreenInit';
const productImage = 'https://cdn.magicpatterns.com/uploads/pQkP3xwrhrqeJUUPX8W6aF/Untitled_design_(3).png';
const checkoutUrls = {
  base: 'https://checkout.tusbeneficios.shop/cart/51508733280489:1',
  pro: 'https://checkout.tusbeneficios.shop/cart/51508742389993:1'
};
const galleryItems: {
  title: string;
  kind: MathVisualKind;
}[] = [{
  title: 'Fracciones explicadas con dibujos, no solo números',
  kind: 'gallery-fractions'
}, {
  title: 'Porcentajes y regla de tres, con ejemplos de la vida real',
  kind: 'gallery-percentages'
}, {
  title: 'Ecuaciones resueltas paso a paso, sin saltos',
  kind: 'gallery-equations'
}, {
  title: 'Geometría con fórmulas y ejemplos visuales',
  kind: 'gallery-geometry'
}, {
  title: 'Potencias claras y listas para practicar',
  kind: 'gallery-powers'
}, {
  title: 'Plano cartesiano y probabilidad a simple vista',
  kind: 'gallery-probability'
}];
const volumes = [{
  title: 'TOMO 1 — Fracciones y Decimales',
  icon: <PercentIcon className="h-5 w-5" data-id="element-32" />,
  quote: 'Nunca más confundir fracciones propias, impropias y equivalentes.',
  items: ['Tipos de fracciones explicados con dibujos', 'Operaciones paso a paso', 'Conversión entre fracciones y decimales', 'Problemas resueltos y errores comunes'],
  alt: 'Mapa mental de fracciones con ejemplos visuales',
  visual: <MathVisual kind="volume-fractions" data-id="element-33" />
}, {
  title: 'TOMO 2 — Porcentajes y Regla de Tres',
  icon: <TrendingUpIcon className="h-5 w-5" data-id="element-34" />,
  quote: 'Porcentajes y regla de tres, resueltos con ejemplos de la vida real.',
  items: ['Qué es un porcentaje y cómo calcularlo', 'Regla de tres directa e inversa', 'Descuentos y aumentos', 'Trucos para resolver más rápido'],
  alt: 'Mapa mental de porcentajes y regla de tres',
  visual: <MathVisual kind="volume-rule-three" data-id="element-35" />
}, {
  title: 'TOMO 3 — Ecuaciones',
  icon: <PencilLineIcon className="h-5 w-5" data-id="element-36" />,
  quote: 'De no entiendo nada a resolver ecuaciones solo/a.',
  items: ['Qué es una ecuación y para qué sirve', 'Cómo despejar la incógnita', 'Ecuaciones de primer grado', 'Errores comunes al despejar'],
  alt: 'Mapa mental de ecuaciones con ejemplos',
  visual: <MathVisual kind="volume-equations" data-id="element-37" />
}, {
  title: 'TOMO 4 — Geometría',
  icon: <MapPinnedIcon className="h-5 w-5" data-id="element-38" />,
  quote: 'Figuras, perímetro y área, explicados con dibujos, no solo fórmulas.',
  items: ['Figuras planas y sus propiedades', 'Cómo calcular perímetro y área', 'Figuras del espacio', 'Fórmulas para repasar rápido'],
  alt: 'Mapa mental de geometría',
  visual: <MathVisual kind="volume-geometry" data-id="element-39" />
}, {
  title: 'TOMO 5 — Potencias y Raíces',
  icon: <SparklesIcon className="h-5 w-5" data-id="element-40" />,
  quote: 'Exponentes y raíces, de una vez por todas claros.',
  items: ['Potencias y sus propiedades', 'Raíces cuadradas y cúbicas', 'Reglas para multiplicar y dividir', 'Tabla de potencias para repasar'],
  alt: 'Mapa mental de potencias',
  visual: <MathVisual kind="volume-roots" data-id="element-41" />
}, {
  title: 'TOMO 6 — Plano Cartesiano y Probabilidad',
  icon: <TrendingUpIcon className="h-5 w-5" data-id="element-42" />,
  quote: 'Coordenadas y probabilidad, con ejemplos que se entienden a simple vista.',
  items: ['Ejes y cómo ubicar puntos', 'Qué es la probabilidad', 'Nociones básicas de estadística', 'Ejercicios de aplicación práctica'],
  alt: 'Mapa mental de plano cartesiano y probabilidad',
  visual: <MathVisual kind="volume-cartesian" data-id="element-43" />
}];
const bonuses = [{
  title: 'Tablero de Tablas de Multiplicar Imprimible',
  eyebrow: 'BONO 01 · $8.000 GRATIS',
  icon: <BookOpenCheckIcon className="h-5 w-5" data-id="element-44" />,
  description: 'Para pegar en el cuaderno o la mesa de estudio y tenerlo siempre a mano. Un formato pensado para memorizar rápido, sin depender de la pantalla.',
  visual: <MathVisual kind="bonus-tables" data-id="element-45" />
}, {
  title: 'Checklist de Tarea de 5 Minutos',
  eyebrow: 'BONO 02 · $7.000 GRATIS',
  icon: <ClipboardCheckIcon className="h-5 w-5" data-id="element-46" />,
  description: 'El repaso rápido antes de entregar cualquier tarea de matemática, para que revise solo/a que no le falta nada.',
  visual: <MathVisual kind="bonus-checklist" data-id="element-47" />
}, {
  title: 'Calendario Mensual de Repaso',
  eyebrow: 'BONO 03 · $9.000 GRATIS',
  icon: <CalendarCheck2Icon className="h-5 w-5" data-id="element-48" />,
  description: 'Un mes entero de repaso ya organizado por tema. Para estudiar de forma constante y no solo la noche antes.',
  visual: <MathVisual kind="bonus-calendar" data-id="element-49" />
}, {
  title: 'Banco de Problemas Resueltos',
  eyebrow: 'BONO 04 · $8.000 GRATIS',
  icon: <FileTextIcon className="h-5 w-5" data-id="element-50" />,
  description: 'Ejercicios resueltos paso a paso, listos para practicar y entender el cómo antes de hacerlo solo/a.',
  visual: <MathVisual kind="bonus-problems" data-id="element-51" />
}];
const reviews = [['Mi hijo se trababa con las fracciones desde 4to grado. Con los mapas, en dos semanas las entendió solo, sin que yo tuviera que explicarle nada.', 'Marina G. · Mamá de un nene de 4to grado'], ['Lo uso con mi hija que está en secundaria y le cuesta álgebra. Las ecuaciones explicadas así, paso a paso, le cerraron mucho más que en el colegio.', 'Diego R. · Papá de una nena de 1er año'], ['Ya no peleamos a la hora de hacer la tarea. Abrimos el mapa del tema y en diez minutos lo entiende.', 'Sabrina T. · Mamá de dos, primaria y secundaria']];
const faqs = [['¿Sirve para primaria y para secundaria?', 'Sí. Los 6 tomos están organizados por nivel de dificultad, desde conceptos básicos de primaria hasta álgebra y geometría de secundaria.'], ['¿Tengo que explicarle yo el contenido a mi hijo/a?', 'No necesariamente. Los mapas están armados para que el chico/a los entienda solo/a, con dibujos y ejemplos resueltos paso a paso.'], ['¿Cómo recibo el material?', 'Acceso inmediato después del pago. Es digital y se puede usar desde celular, tablet o computadora. También lo podés imprimir.'], ['¿Qué diferencia hay entre los dos planes?', 'El Sistema Base tiene los 6 tomos y 4 bonos. El Plan PRO agrega herramientas para seguir el progreso y preparar exámenes.'], ['¿Está actualizado con lo que se enseña en el colegio?', 'Sí. El contenido está armado en base a los temas principales exigidos en escuelas argentinas de primaria y secundaria.'], ['¿Tiene garantía real?', 'Sí. Tenés 7 días, sin preguntas. Te devolvemos el 100%. Menos del 1% pide devolución.']];
export function HomePage() {
  useScreenInit();
  const [seconds, setSeconds] = useState(86399);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [openVolume, setOpenVolume] = useState(0);
  const [openBonus, setOpenBonus] = useState<number | null>(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [plan, setPlan] = useState<'base' | 'pro'>('pro');
  useEffect(() => {
    const tick = window.setInterval(() => setSeconds(current => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(tick);
  }, []);
  const time = getTimeParts(seconds);
  function scrollToPricing() {
    document.getElementById('precios')?.scrollIntoView({
      behavior: 'smooth'
    });
  }
  function goToCheckout() {
    window.location.assign(checkoutUrls[plan]);
  }
  function nextGallery(direction: number) {
    setGalleryIndex(current => (current + direction + galleryItems.length) % galleryItems.length);
  }
  return <main className="w-full overflow-hidden bg-white font-sans text-[#1B3A6B]" data-id="element-52">
      <MetaPixel data-id="element-53" />
      <div className="sticky top-0 z-50 bg-[#FFC72C] px-4 py-2 text-center text-xs font-extrabold tracking-tight text-[#1B3A6B]" data-id="element-54">
        <TimerIcon className="mr-1 inline h-3.5 w-3.5" data-id="element-55" />
        Precio de lanzamiento — cierra en:{' '}
        <span className="tabular-nums" data-id="element-56">{formatSmallTime(time)}</span>
      </div>

      <section className="bg-[#F0F3F7] px-5 pb-8 pt-8 text-center sm:pt-12" data-id="element-57">
        <div className="mx-auto max-w-xl" data-id="element-58">
          <p className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-extrabold text-[#1B3A6B] shadow-sm" data-id="element-59">
            <GraduationCapIcon className="h-4 w-4 text-[#1B3A6B]" data-id="element-60" /> De Primaria
            a Secundaria · Edición 2026
          </p>
          <h1 className="text-[2rem] font-black leading-[1.04] tracking-[-0.045em] text-[#1B3A6B] sm:text-5xl" data-id="element-61">
            TUS CLASES DE MATEMÁTICAS{' '}
            <span className="mt-1 inline-block rounded-lg bg-[#FFC72C] px-2 py-0.5 text-[#1B3A6B] shadow-[4px_4px_0_#1B3A6B]" data-id="element-62">
              RESUELTAS
            </span>
          </h1>

          <img src={productImage} alt="Mate Fácil PRO: libro y mapas mentales de matemática" className="mx-auto my-5 w-full max-w-[390px] object-contain sm:max-w-[440px]" data-id="element-63" />

          <p className="mx-auto max-w-lg text-[15px] leading-6 text-[#4A5568]" data-id="element-64">
            +50 mapas mentales para que tu hijo/a entienda matemática de una
            vez, sin frustrarse y sin que vos tengas que reinventarte como profe
            en la mesa de la cocina.
          </p>
          <div className="mt-6 grid gap-3" data-id="element-65">
            <button type="button" onClick={scrollToPricing} className="flex h-14 items-center justify-center gap-2 rounded-xl bg-[#1B3A6B] px-5 text-base font-extrabold text-white shadow-lg shadow-[#1B3A6B]/20 transition-transform active:scale-[0.98]" data-id="element-66">
              <BrainCircuitIcon className="h-5 w-5 text-[#FFC72C]" data-id="element-67" /> Quiero
              Mate Fácil PRO
            </button>
            <a href="#tomos" className="flex h-12 items-center justify-center rounded-xl border-2 border-[#1B3A6B] px-5 text-sm font-extrabold text-[#1B3A6B]" data-id="element-68">
              Ver qué incluye ↓
            </a>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs font-bold text-[#4A5568]" data-id="element-69">
            <span className="inline-flex items-center gap-1" data-id="element-70">
              <StarIcon className="h-3.5 w-3.5 fill-[#FFC72C] text-[#FFC72C]" data-id="element-71" />
              4.9/5
            </span>
            <span data-id="element-72">+900 familias</span>
            <span data-id="element-73">+50 mapas mentales</span>
            <span className="inline-flex items-center gap-1" data-id="element-74">
              <LockKeyholeIcon className="h-3.5 w-3.5" data-id="element-75" />
              Garantía 7 días
            </span>
          </div>
        </div>
      </section>

      <section className="px-5 py-12" data-id="element-76">
        <div className="mx-auto max-w-xl text-center" data-id="element-77">
          <p className="mb-5 text-sm font-extrabold text-[#1B3A6B]" data-id="element-78">
            Así se ve cuando la matemática por fin se entiende
          </p>
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-[#F0F3F7]" data-id="element-79">
            <AnimatePresence mode="wait" data-id="element-80">
              <motion.div key={galleryIndex} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.24
            }} data-id="element-81">
                <MathVisual kind={galleryItems[galleryIndex].kind} data-id="element-82" />
              </motion.div>
            </AnimatePresence>
            <button aria-label="Imagen anterior" type="button" onClick={() => nextGallery(-1)} className="absolute left-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/80 text-[#1B3A6B] shadow-sm" data-id="element-83">
              <ChevronLeftIcon data-id="element-84" />
            </button>
            <button aria-label="Siguiente imagen" type="button" onClick={() => nextGallery(1)} className="absolute right-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/80 text-[#1B3A6B] shadow-sm" data-id="element-85">
              <ChevronRightIcon data-id="element-86" />
            </button>
          </div>
          <p className="mt-4 min-h-10 text-sm font-bold text-[#4A5568]" data-id="element-87">
            {galleryItems[galleryIndex].title}
          </p>
          <div className="mt-3 flex justify-center gap-1.5" aria-label="Seleccionar imagen" data-id="element-88">
            {galleryItems.map((item, index) => <button key={item.title} type="button" onClick={() => setGalleryIndex(index)} className={`h-2 rounded-full transition-all ${index === galleryIndex ? 'w-5 bg-[#1B3A6B]' : 'w-2 bg-slate-300'}`} aria-label={`Ver imagen ${index + 1}`} data-id="element-89" />)}
          </div>
        </div>
      </section>

      <section className="px-5 py-12" data-id="element-90">
        <div className="mx-auto max-w-xl" data-id="element-91">
          <SectionHeading title="Lo que dicen los papás que ya lo usan" data-id="element-92" />
          <div className="mt-7 space-y-3" data-id="element-93">
            {reviews.map(([quote, author]) => <figure className="rounded-2xl border border-slate-200 p-5 shadow-sm" key={author} data-id="element-94">
                <div className="mb-3 flex gap-0.5 text-[#FFC72C]" data-id="element-95">
                  {Array.from({
                length: 5
              }).map((_, index) => <StarIcon className="h-4 w-4 fill-current" key={index} data-id="element-96" />)}
                </div>
                <blockquote className="text-[15px] font-medium leading-6 text-[#1B3A6B]" data-id="element-97">
                  “{quote}”
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold text-[#4A5568]" data-id="element-98">
                  — {author}
                </figcaption>
              </figure>)}
          </div>
        </div>
      </section>

      <section className="bg-[#1B3A6B] px-5 py-12 text-white" data-id="element-99">
        <div className="mx-auto max-w-xl" data-id="element-100">
          <SectionHeading title="¿Este método es para tu familia?" inverse data-id="element-101" />
          <div className="mt-7 space-y-3" data-id="element-102">
            <AudienceCard icon={<UserRoundCheckIcon data-id="element-104" />} title="Papás de chicos de primaria" text="Tu hijo/a se traba con tablas, fracciones o problemas y no sabés cómo explicárselo distinto. Tenés el mapa visual que lo destraba en minutos." bullets={['Conceptos básicos con dibujos', 'Ejercicios resueltos paso a paso']} data-id="element-103" />
            <AudienceCard icon={<BookOpenCheckIcon data-id="element-106" />} title="Papás de chicos de secundaria" text="Álgebra, ecuaciones, geometría... las tardes de tarea se vuelven una pelea. Repasa lo que no entendió sin depender de una clase particular." bullets={['Ecuaciones y álgebra paso a paso', 'Repaso rápido antes de la prueba']} data-id="element-105" />
            <AudienceCard icon={<GraduationCapIcon data-id="element-108" />} title="Papás que quieren cubrir toda la etapa escolar" text="Un solo sistema para acompañar varios años de colegio, incluso si tenés más de un hijo/a." bullets={['Contenido por nivel de dificultad', 'Un material para varios años']} data-id="element-107" />
          </div>
        </div>
      </section>

      <section className="px-5 py-12" data-id="element-109">
        <div className="mx-auto max-w-xl" data-id="element-110">
          <SectionHeading title="¿Cuántas tardes más se van a pelear por la tarea de mate?" data-id="element-111" />
          <p className="mt-3 text-center text-[15px] leading-6 text-[#4A5568]" data-id="element-112">
            Sin el método correcto, siempre termina igual.
          </p>
          <ul className="mt-7 space-y-3 text-[15px] leading-6 text-[#4A5568]" data-id="element-113">
            {['Tu hijo/a hace la tarea, pero no entiende lo que está haciendo', 'Le explicás vos y terminan los dos frustrados', 'Estudia la noche anterior y se le olvida todo', 'No sabés cómo explicarle un tema distinto al colegio', 'Cada año el temario cambia y no tenés material actualizado', 'Ves que le va bajando la nota y no sabés por dónde reforzar'].map(item => <li className="flex gap-3" key={item} data-id="element-114">
                <XIcon className="mt-1 h-4 w-4 shrink-0 text-red-500" data-id="element-115" />
                {item}
              </li>)}
          </ul>
          <p className="mt-7 rounded-2xl bg-[#FFC72C] p-5 text-center text-base font-extrabold leading-6 text-[#1B3A6B]" data-id="element-116">
            No es falta de esfuerzo. Es falta de un método visual. Con Mate
            Fácil PRO, tu hijo/a por fin entiende, no solo memoriza.
          </p>
        </div>
      </section>

      <section id="tomos" className="bg-[#F0F3F7] px-5 py-12" data-id="element-117">
        <div className="mx-auto max-w-xl" data-id="element-118">
          <div className="text-center" data-id="element-119">
            <span className="inline-flex rounded-full bg-[#FFC72C] px-3 py-1 text-xs font-extrabold text-[#1B3A6B]" data-id="element-120">
              <PackageCheckIcon className="mr-1 h-3.5 w-3.5" data-id="element-121" />
              Sistema completo
            </span>
            <SectionHeading title="6 tomos. +50 mapas mentales. Todo resuelto." data-id="element-122" />
            <p className="mt-3 text-[15px] text-[#4A5568]" data-id="element-123">
              De la tarea en blanco al “ya entendí”.
            </p>
          </div>
          <div className="mt-7 space-y-3" data-id="element-124">
            {volumes.map((volume, index) => <AccordionItem key={volume.title} {...volume} isOpen={openVolume === index} onToggle={() => setOpenVolume(openVolume === index ? -1 : index)} data-id="element-125" />)}
          </div>
          <p className="mt-6 text-center text-xs font-bold leading-5 text-[#4A5568]" data-id="element-126">
            Cada tomo incluye explicaciones simples, ejemplos resueltos,
            contenido por nivel y está adaptado a lo que se enseña en Argentina.
          </p>
        </div>
      </section>

      <section className="bg-[#FFC72C] px-5 py-12" data-id="element-127">
        <div className="mx-auto max-w-xl" data-id="element-128">
          <SectionHeading title="4 bonos incluidos. Gratis. Siempre." data-id="element-129" />
          <p className="mt-3 text-center text-sm font-semibold text-[#1B3A6B]" data-id="element-130">
            Valor total: <s data-id="element-131">$32.000 ARS</s> → $0 con tu sistema.
          </p>
          <div className="mt-7 space-y-3" data-id="element-132">
            {bonuses.map((bonus, index) => <AccordionItem key={bonus.title} {...bonus} isOpen={openBonus === index} onToggle={() => setOpenBonus(openBonus === index ? null : index)} data-id="element-133" />)}
          </div>
        </div>
      </section>

      <section className="px-5 py-12" data-id="element-134">
        <div className="mx-auto max-w-xl" data-id="element-135">
          <SectionHeading title="Sin Mate Fácil PRO vs. Con Mate Fácil PRO" data-id="element-136" />
          <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200 text-sm" data-id="element-137">
            <div className="grid grid-cols-2 bg-[#1B3A6B] font-extrabold text-white" data-id="element-138">
              <div className="p-3" data-id="element-139">Sin el sistema</div>
              <div className="border-l border-white/20 p-3 text-[#FFC72C]" data-id="element-140">
                Con el sistema
              </div>
            </div>
            {[['Memoriza sin entender', 'Mapas visuales que explican el por qué'], ['Te toca explicarle vos', 'Explicaciones simples, listas para usar'], ['Repasa solo antes de la prueba', 'Calendario de repaso organizado'], ['No tenés material actualizado', 'Contenido de primaria a secundaria'], ['La tarea es una pelea', 'Estudia solo/a, sin ayuda extra']].map(([bad, good]) => <div key={bad} className="grid grid-cols-2 border-t border-slate-200 text-[#4A5568]" data-id="element-141">
                <div className="flex gap-2 p-3 leading-5" data-id="element-142">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-143" />
                  {bad}
                </div>
                <div className="flex gap-2 border-l border-slate-200 p-3 font-semibold leading-5 text-[#1B3A6B]" data-id="element-144">
                  <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" data-id="element-145" />
                  {good}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <section id="precios" className="bg-[#1B3A6B] px-5 py-12 text-white" data-id="element-146">
        <div className="mx-auto max-w-xl" data-id="element-147">
          <p className="text-center text-xs font-extrabold tracking-wider text-[#FFC72C]" data-id="element-148">
            ESTA OFERTA TERMINA EN
          </p>
          <div className="mt-3 flex justify-center gap-2" aria-label="Tiempo restante" data-id="element-149">
            {([time.hours, time.minutes, time.seconds] as number[]).map((part, index) => <div className="text-center" key={index} data-id="element-150">
                  <div className="rounded-xl bg-[#142B52] px-3 py-2 text-3xl font-black tabular-nums sm:text-4xl" data-id="element-151">
                    {String(part).padStart(2, '0')}
                  </div>
                  <span className="mt-1 block text-[10px] font-bold text-slate-300" data-id="element-152">
                    {['HORAS', 'MIN', 'SEG'][index]}
                  </span>
                </div>)}
          </div>
          <div className="mt-9 text-center" data-id="element-153">
            <h2 className="text-3xl font-black tracking-tight" data-id="element-154">
              ELEGÍ TU MATE FÁCIL PRO
            </h2>
            <p className="mt-2 text-sm text-[#FFE28A]" data-id="element-155">
              Ambas opciones incluyen los 6 tomos y los 4 bonos base
            </p>
          </div>
          <div className="mt-7 space-y-3" data-id="element-156">
            <PlanCard selected={plan === 'base'} onClick={() => setPlan('base')} name="SISTEMA BASE" description="6 tomos + 4 bonos estratégicos" original="$39.000" price="$18.990" data-id="element-157" />
            <PlanCard selected={plan === 'pro'} onClick={() => setPlan('pro')} name="PLAN PRO" description="6 tomos + 4 bonos + 3 herramientas de acompañamiento PRO" original="$59.000" price="$27.990" pro data-id="element-158" />
          </div>
          <button type="button" onClick={goToCheckout} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#FFC72C] text-lg font-black text-[#1B3A6B] transition-transform active:scale-[0.98]" data-id="element-159">
            SÍ, QUIERO EL {plan === 'pro' ? 'PLAN PRO' : 'SISTEMA BASE'}{' '}
            <ArrowRightIcon className="h-5 w-5" data-id="element-160" />
          </button>
          <p className="mt-3 text-center text-xs text-slate-300" data-id="element-161">
            La opción más elegida por papás que quieren acompañar de cerca el
            progreso escolar
          </p>
          <p className="mt-5 flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs font-bold text-slate-200" data-id="element-162">
            <span data-id="element-163">
              <LockKeyholeIcon className="mr-1 inline h-3.5 w-3.5 text-[#FFC72C]" data-id="element-164" />
              Pago seguro
            </span>
            <span data-id="element-165">⚡ Acceso inmediato</span>
            <span data-id="element-166">
              <ShieldCheckIcon className="mr-1 inline h-3.5 w-3.5 text-[#FFC72C]" data-id="element-167" />
              Garantía 7 días
            </span>
          </p>
        </div>
      </section>

      <section className="bg-[#F0F3F7] px-5 py-12" data-id="element-168">
        <div className="mx-auto max-w-xl" data-id="element-169">
          <SectionHeading title="Qué vas a obtener con el PLAN PRO" data-id="element-170" />
          <p className="mt-3 text-center text-[15px] leading-6 text-[#4A5568]" data-id="element-171">
            La diferencia entre tener los mapas sueltos y un sistema completo
            para acompañar a tu hijo/a.
          </p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2" data-id="element-172">
            <FeatureColumn title="SI NO TENÉS EL PLAN PRO" items={['Solo tenés los mapas mentales en PDF', 'Sin herramientas de seguimiento', 'No sabés en qué tema se traba', 'Sin guía para pruebas', 'Sin actualizaciones futuras']} data-id="element-173" />
            <FeatureColumn title="SI TENÉS EL PLAN PRO" items={['Seguimiento del progreso por tema', 'Guía para preparar exámenes', 'Recursos para reforzar dificultades', 'Acceso a futuras actualizaciones']} pro data-id="element-174" />
          </div>
          <div className="mt-5 space-y-3" data-id="element-175">
            <ProTool icon={<TrendingUpIcon data-id="element-177" />} title="Seguimiento de Progreso por Tema" text="Planilla lista para anotar qué temas ya domina y cuáles necesita repasar más." data-id="element-176" />
            <ProTool icon={<ClipboardCheckIcon data-id="element-179" />} title="Guía para Preparar Exámenes" text="Paso a paso para armar un plan de estudio los días previos a una prueba." data-id="element-178" />
            <ProTool icon={<InfinityIcon data-id="element-181" />} title="Actualizaciones de por Vida" text="Acceso gratuito a cada nuevo mapa mental o material que sumemos al sistema." data-id="element-180" />
          </div>
        </div>
      </section>

      <section className="px-5 py-12 text-center" data-id="element-182">
        <div className="mx-auto max-w-xl" data-id="element-183">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-[#FFC72C]" data-id="element-184" />
          <h2 className="mt-4 text-2xl font-black tracking-tight text-[#1B3A6B]" data-id="element-185">
            Garantía 7 días — Sin preguntas
          </h2>
          <p className="mt-3 text-[15px] leading-6 text-[#4A5568]" data-id="element-186">
            Descargás el material. Lo usás con tu hijo/a. Si en 7 días no es lo
            que esperabas, te devolvemos el 100% sin formularios, sin drama.
          </p>
          <span className="mt-5 inline-block rounded-full bg-[#FFC72C] px-4 py-2 text-xs font-extrabold text-[#1B3A6B]" data-id="element-187">
            Tasa de devoluciones: menos del 1%
          </span>
        </div>
      </section>

      <section className="bg-[#F0F3F7] px-5 py-12" data-id="element-188">
        <div className="mx-auto max-w-xl" data-id="element-189">
          <SectionHeading title="Lo que más nos preguntan" data-id="element-190" />
          <div className="mt-7 space-y-3" data-id="element-191">
            {faqs.map(([question, answer], index) => <AccordionItem key={question} title={question} icon={<div className="h-5 w-5" data-id="element-193" />} description={answer} isOpen={openFaq === index} onToggle={() => setOpenFaq(openFaq === index ? null : index)} data-id="element-192" />)}
          </div>
        </div>
      </section>

      <section className="bg-[#1B3A6B] px-5 py-14 text-center text-white" data-id="element-194">
        <div className="mx-auto max-w-xl" data-id="element-195">
          <h2 className="text-3xl font-black leading-tight tracking-tight" data-id="element-196">
            La próxima tarea de mate ya puede estar resuelta.
          </h2>
          <div className="mt-7 grid gap-3" data-id="element-197">
            <button type="button" onClick={scrollToPricing} className="flex h-14 items-center justify-center gap-2 rounded-xl bg-[#FFC72C] font-extrabold text-[#1B3A6B]" data-id="element-198">
              <BrainCircuitIcon className="h-5 w-5" data-id="element-199" />
              Quiero Mate Fácil PRO
            </button>
            <button type="button" onClick={scrollToPricing} className="flex h-12 items-center justify-center rounded-xl border border-white/50 font-extrabold" data-id="element-200">
              Quiero el Plan PRO
            </button>
          </div>
          <p className="mt-5 text-xs font-bold text-slate-200" data-id="element-201">
            Garantía 7 días · Pago seguro · Acceso inmediato
          </p>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#142B52] px-5 py-10 text-center text-white" data-id="element-202">
        <div className="mx-auto max-w-xl" data-id="element-203">
          <p className="text-lg font-black" data-id="element-204">Mate Fácil™ PRO</p>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-300" data-id="element-205">
            El sistema N°1 para que los chicos de primaria y secundaria
            entiendan matemática, sin frustrarse y sin depender de una clase
            particular.
          </p>
          <p className="mt-5 text-xs font-semibold text-[#FFC72C]" data-id="element-206">
            contacto@matefacilpro.com · @matefacilpro · matefacilpro.com
          </p>
          <p className="mt-6 text-xs text-slate-400" data-id="element-207">
            © Mate Fácil™ PRO 2026 — Todos los derechos reservados.
          </p>
          <p className="mt-3 text-sm font-bold text-white" data-id="element-208">
            “Menos frustración, más notas altas.”
          </p>
        </div>
      </footer>
    </main>;
}
function SectionHeading({
  title,
  inverse = false
}: {
  title: string;
  inverse?: boolean;
}) {
  return <h2 className={`text-center text-2xl font-black leading-tight tracking-[-0.025em] sm:text-3xl ${inverse ? 'text-white' : 'text-[#1B3A6B]'}`} data-id="element-209">
      {title}
    </h2>;
}
function AudienceCard({
  icon,
  title,
  text,
  bullets
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  bullets: string[];
}) {
  return <article className="rounded-2xl bg-white p-5 text-[#1B3A6B]" data-id="element-210">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFC72C]" data-id="element-211">
        {icon}
      </div>
      <h3 className="font-extrabold" data-id="element-212">{title}</h3>
      <p className="mt-2 text-sm leading-5 text-[#4A5568]" data-id="element-213">{text}</p>
      <ul className="mt-4 space-y-1.5 text-xs font-bold" data-id="element-214">
        {bullets.map(bullet => <li key={bullet} data-id="element-215">
            <CheckCircle2Icon className="mr-1.5 inline h-3.5 w-3.5 text-green-600" data-id="element-216" />
            {bullet}
          </li>)}
      </ul>
    </article>;
}
function PlanCard({
  selected,
  onClick,
  name,
  description,
  original,
  price,
  pro = false
}: {
  selected: boolean;
  onClick: () => void;
  name: string;
  description: string;
  original: string;
  price: string;
  pro?: boolean;
}) {
  return <button type="button" onClick={onClick} className={`relative w-full rounded-2xl border-2 p-4 text-left transition-colors ${selected ? 'border-[#FFC72C] bg-[#23487e]' : 'border-white/30 bg-white/5'}`} data-id="element-217">
      {pro && <span className="absolute -top-3 right-4 rounded-full bg-[#FFC72C] px-2.5 py-1 text-[10px] font-black text-[#1B3A6B]" data-id="element-218">
          MÁS ELEGIDO
        </span>}
      <span className="flex gap-3" data-id="element-219">
        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-[#FFC72C]' : 'border-white'}`} data-id="element-220">
          {selected && <span className="h-2.5 w-2.5 rounded-full bg-[#FFC72C]" data-id="element-221" />}
        </span>
        <span data-id="element-222">
          <span className="block text-sm font-black" data-id="element-223">
            {pro && '🚀 '}
            {name}
          </span>
          <span className="mt-1 block text-xs leading-5 text-slate-200" data-id="element-224">
            {description}
          </span>
          {pro && <span className="mt-2 inline-block rounded bg-[#142B52] px-2 py-1 text-[10px] font-extrabold text-white" data-id="element-225">
              AHORRÁS $32.000
            </span>}
          <span className="mt-3 block" data-id="element-226">
            <s className="mr-2 text-sm text-slate-300" data-id="element-227">{original}</s>
            <strong className="text-2xl text-[#FFC72C]" data-id="element-228">{price}</strong>
          </span>
        </span>
      </span>
    </button>;
}
function FeatureColumn({
  title,
  items,
  pro = false
}: {
  title: string;
  items: string[];
  pro?: boolean;
}) {
  return <article className={`rounded-2xl border p-5 ${pro ? 'border-[#FFC72C] bg-[#1B3A6B] text-white' : 'border-slate-200 bg-white text-[#1B3A6B]'}`} data-id="element-229">
      <p className={`mb-4 text-xs font-black ${pro ? 'text-[#FFC72C]' : 'text-[#4A5568]'}`} data-id="element-230">
        {pro ? '✓ ' : '✕ '}
        {title}
      </p>
      <ul className="space-y-3 text-sm leading-5" data-id="element-231">
        {items.map(item => <li className="flex gap-2" key={item} data-id="element-232">
            {pro ? <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#FFC72C]" data-id="element-233" /> : <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-234" />}
            {item}
          </li>)}
      </ul>
    </article>;
}
function ProTool({
  icon,
  title,
  text
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return <article className="flex gap-4 rounded-2xl bg-white p-4" data-id="element-235">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFC72C] text-[#1B3A6B]" data-id="element-236">
        {icon}
      </span>
      <div data-id="element-237">
        <h3 className="font-extrabold text-[#1B3A6B]" data-id="element-238">{title}</h3>
        <p className="mt-1 text-sm leading-5 text-[#4A5568]" data-id="element-239">{text}</p>
      </div>
    </article>;
}
function getTimeParts(totalSeconds: number) {
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60
  };
}
function formatSmallTime(time: {
  hours: number;
  minutes: number;
  seconds: number;
}) {
  return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}:${String(time.seconds).padStart(2, '0')}`;
}